import { useEffect, useMemo, useState } from 'react';
import { useCancelInferenceJobMutation, useCompleteInferenceJobMutation, useGetActiveDocumentUploadQuery, useRetryInferenceJobMutation, useUploadDocumentMutation } from '@/services/ApiService.ts';
import { EnrichedSkill } from '@/apiTypes/SkillInferenceTypes.ts';
import { isJobStalled } from './skillsUtils';
import { toast } from '@/lib/toast';
import { useUser } from '@/hooks/user/useUser';
import {
    EMPTY_FILE_COPY,
    FILE_TOO_LARGE_COPY,
    GENERIC_UPLOAD_FAILURE_COPY,
    INVALID_FILE_TYPE_COPY,
    UNOPENABLE_DOCUMENT_COPY,
    UNREADABLE_DOCUMENT_COPY,
} from '@/constants/documentUploadCopy';

export type SkillInferenceUploadStatus = 'checking' | 'ready' | 'processing' | 'reviewing' | 'failed';

export interface UseSkillInferenceResult {
    uploadStatus: SkillInferenceUploadStatus;
    jobId: string | undefined;
    skills: EnrichedSkill[];
    handleUpload: (file: File) => Promise<void>;
    handleComplete: (acceptedSkillIds: string[]) => Promise<boolean>;
    handleRetry: () => Promise<void>;
    handleCancel: () => Promise<boolean>;
    isCancelling: boolean;
    resetToReady: () => Promise<void>;
    isResettingToReady: boolean;
    canRetry: boolean;
    uploadError: string | undefined;
}

const POLLING_INTERVAL_MS = 3000;

/**
 * User-facing copy for the rejection reasons the API returns as a machine-readable `reason`. Owned
 * here rather than rendered from the server message so wording stays editable in the frontend.
 */
const SERVER_REJECTION_COPY: Record<string, string> = {
    empty_document_text: UNREADABLE_DOCUMENT_COPY,
    empty_file: EMPTY_FILE_COPY,
    magic_bytes_mismatch: UNOPENABLE_DOCUMENT_COPY,
    // The client blocks both of these before upload, so these only surface for a non-browser
    // caller or a stale bundle — worth a real message rather than "something went wrong".
    invalid_mime_type: INVALID_FILE_TYPE_COPY,
    file_too_large: FILE_TOO_LARGE_COPY,
};

/**
 * Own-property check rather than a bare index: an unexpected `reason` of `toString` or `constructor`
 * would otherwise resolve to a function, which `setUploadError` treats as a state updater. An
 * empty-string reason has to fall through too, or the user is shown no error at all.
 */
const copyForRejection = (reason: string | undefined): string =>
    (reason && Object.prototype.hasOwnProperty.call(SERVER_REJECTION_COPY, reason))
        ? SERVER_REJECTION_COPY[reason]
        : GENERIC_UPLOAD_FAILURE_COPY;

/** Reads the API's machine-readable rejection code out of an RTK Query error body. */
const readRejectionReason = (error: unknown): string | undefined => {
    if (typeof error !== 'object' || error === null || !('data' in error)) return undefined;

    const { data } = error;

    if (typeof data !== 'object' || data === null || !('reason' in data)) return undefined;

    return typeof data.reason === 'string' ? data.reason : undefined;
};

const UPLOAD_MUTATION_CACHE_KEY = 'skill-inference-upload';

export const useSkillInference = (): UseSkillInferenceResult => {
    const [isPolling, setIsPolling] = useState(false);
    const [uploadError, setUploadError] = useState<string | undefined>(undefined);
    const [isResettingToReady, setIsResettingToReady] = useState(false);

    const { user, isLoading: isUserLoading } = useUser();
    // `Layout` mounts this for anonymous visitors too, where the GET can only 401 and then fail the
    // session refresh it triggers.
    const { data, isLoading, isError } = useGetActiveDocumentUploadQuery(undefined, {
        pollingInterval: isPolling ? POLLING_INTERVAL_MS : 0,
        skip: !user,
    });
    // `fixedCacheKey` shares this mutation's in-flight state across every `useSkillInference`
    // caller. Without it only the instance that called `handleUpload` sees `isUploading`, so a
    // consumer rendered elsewhere in the tree (the sheet's "Cancel extraction" footer) reports
    // 'ready' for the whole POST and drops out until the invalidated GET lands.
    const [uploadDocument, { isLoading: isUploading }] = useUploadDocumentMutation({ fixedCacheKey: UPLOAD_MUTATION_CACHE_KEY });
    const [completeInferenceJob, { isLoading: isCompleting }] = useCompleteInferenceJobMutation();
    const [retryInferenceJob] = useRetryInferenceJobMutation();
    const [cancelInferenceJob, { isLoading: isCancelling }] = useCancelInferenceJobMutation();

    const activeJob = data?.activeJob;

    // Defensive: the backend bounds every job at 15 min, but if a stale 'processing' job ever
    // slips through, stop polling and surface the failed UI.
    const isStalled = isJobStalled(activeJob);

    useEffect(() => {
        const status = activeJob?.status;

        setIsPolling((status === 'queued' || status === 'processing') && !isStalled);
    }, [activeJob?.status, isStalled]);

    const handleUpload = async (file: File) => {
        const formData = new FormData();
        formData.append('document', file);

        const result = await uploadDocument(formData);

        if ('error' in result) {
            const reason = readRejectionReason(result.error);

            setUploadError(copyForRejection(reason));
        }
        else {
            setIsPolling(true);
        }
    };

    const handleComplete = async (acceptedSkillIds: string[]): Promise<boolean> => {
        const result = await completeInferenceJob({ skillIds: acceptedSkillIds });

        // Return success/failure only; the caller owns the error toast/UI so it can stay
        // in sync with its own loading/redirect flow.
        return !('error' in result);
    };

    const handleRetry = async () => {
        setUploadError(undefined);

        const result = await retryInferenceJob();

        if ('error' in result) {
            const message = 'Retry failed. Please try again.';
            setUploadError(message);
            toast.error(message);
        }
        else {
            setIsPolling(true);
        }
    };

    const handleCancel = async (): Promise<boolean> => {
        setUploadError(undefined);

        const result = await cancelInferenceJob();

        // Return success/failure so the caller can keep its confirmation dialog open on
        // failure and only persist the "don't ask again" preference on a real cancel.
        if ('error' in result) {
            toast.error(GENERIC_UPLOAD_FAILURE_COPY);
            return false;
        }

        setIsPolling(false);
        return true;
    };

    const resetToReady = async () => {
        setUploadError(undefined);

        if (!activeJob) return;

        setIsResettingToReady(true);
        try {
            const result = await completeInferenceJob({ skillIds: [] });

            if ('error' in result) {
                toast.error(GENERIC_UPLOAD_FAILURE_COPY);
            }
        }
        finally {
            setIsResettingToReady(false);
        }
    };

    const uploadStatus = useMemo<SkillInferenceUploadStatus>(() => {
        if (isUploading) return 'processing';
        if (isCompleting) return 'checking';
        if (activeJob?.status === 'complete') return 'reviewing';
        if (activeJob?.status === 'failed') return 'failed';
        if (isStalled) return 'failed';
        if (activeJob?.status === 'queued' || activeJob?.status === 'processing' || isPolling) return 'processing';
        if (isError) return 'failed';
        if (isLoading || isUserLoading) return 'checking';
        return 'ready';
    }, [activeJob?.status, isUploading, isCompleting, isPolling, isStalled, isError, isLoading, isUserLoading]);

    return {
        uploadStatus,
        jobId: activeJob?.jobId,
        skills: activeJob?.skills ?? [],
        handleUpload,
        handleComplete,
        handleRetry,
        handleCancel,
        isCancelling,
        resetToReady,
        isResettingToReady,
        canRetry: activeJob?.canRetry ?? false,
        uploadError,
    };
};
